import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import logo5 from './assets/Home/logo5.png';
import logo6 from './assets/Home/logo6.png';
import logo7 from './assets/Home/logo7.png';
import logo8 from './assets/Home/logo8.png';
import logo12 from './assets/Home/logo12.png';
import logo13 from './assets/Home/logo13.png';
import warpcodeLogo from './assets/Home/warpcode.png';
import clinebotLogo from './assets/Home/clinebot.png';
import zeesyLogo from './assets/Home/zeesy.avif';
import campofrioLogo from './assets/Home/campofrio.png';
import apolloLogo from './assets/Home/apollo.avif';
import simplysufiLogo from './assets/Home/simplysufi.png';
import irongearLogo from './assets/Home/irongear.avif';
import partnerNewLogo from './assets/Home/partner-new.png';

// External logo URLs for brands
const LOGO_URLS = {
  bimbo: 'https://upload.wikimedia.org/wikipedia/commons/b/b0/Grupo_Bimbo_Logo.png',
};

const partnerLogosRaw = [
  { name: 'Cadbury', logo: logo5, ticketStyle: 'white' },
  { name: 'Stella Artois', logo: logo6, ticketStyle: 'white' },
  { name: 'Lindt', logo: logo7, ticketStyle: 'white' },
  { name: 'Tiffany & Co', logo: logo8, ticketStyle: 'white' },
  { name: 'SCRIPPS', logo: logo12, ticketStyle: 'dark' },
  { name: 'SIMPLI.FI', logo: logo13, ticketStyle: 'dark' },
  { name: 'Campofrio', logo: campofrioLogo, ticketStyle: 'white' },
  { name: 'Bimbo', logo: LOGO_URLS.bimbo, ticketStyle: 'white' },
  { name: 'Warpcode', logo: warpcodeLogo, ticketStyle: 'white' },
  { name: 'Clinebot', logo: clinebotLogo, ticketStyle: 'white' },
  { name: 'Zeesy', logo: zeesyLogo, ticketStyle: 'white' },
  { name: 'Apollo', logo: apolloLogo, ticketStyle: 'white' },
  { name: 'SimplySufi', logo: simplysufiLogo, ticketStyle: 'white' },
  { name: 'Irongear', logo: irongearLogo, ticketStyle: 'dark' },
  { name: 'Partner', logo: partnerNewLogo, ticketStyle: 'white' },
];

// Exclude logo8 (Tiffany & Co) and logo13 from displayed partners
const partnerLogos = partnerLogosRaw.filter((p) => p.logo !== logo8 && p.logo !== logo13);

const Partners = () => {
  const sectionRef = useRef(null);
  const logoRefs = useRef([]);
  const hasPoppedRef = useRef(false);
  const bubbleTweensRef = useRef([]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    // Responsive padding: smaller on mobile so ellipse fits
    const getPadding = () => (typeof window !== 'undefined' && window.innerWidth < 640 ? 24 : window.innerWidth < 1024 ? 40 : 60);

    const getTargets = () => {
      const rect = section.getBoundingClientRect();
      const padding = getPadding();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const width = rect.width - padding * 2;
      const height = rect.height - padding * 2;
      return { rect, centerX, centerY, width, height, padding };
    };

    // Bubble animation: gentle scale pulse with staggered phase
    const startBubble = () => {
      bubbleTweensRef.current.forEach((t) => t && t.kill());
      bubbleTweensRef.current = [];
      logoRefs.current.forEach((el, i) => {
        if (!el) return;
        const t = gsap.to(el, {
          scale: 1.12,
          duration: 1.8,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
          delay: i * 0.15,
        });
        bubbleTweensRef.current.push(t);
      });
    };

    // Initial state: all at center, scaled down
    const { centerX: cx, centerY: cy } = getTargets();
    logoRefs.current.forEach((el) => {
      if (!el) return;
      gsap.set(el, {
        x: cx,
        y: cy,
        xPercent: -50,
        yPercent: -50,
        scale: 0,
        opacity: 0,
      });
    });

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting || hasPoppedRef.current) return;
        hasPoppedRef.current = true;

        const { centerX: cx2, centerY: cy2, width: w2, height: h2 } = getTargets();
        const n = partnerLogos.length;
        const isLaptop = typeof window !== 'undefined' && window.innerWidth >= 1024 && window.innerWidth < 1536;
        // On laptop/ThinkPad: larger ellipse so tickets stay clear of tagline; keep min radii for small screens
        const minRx = w2 < 400 ? 50 : w2 < 640 ? 80 : 100;
        const minRy = h2 < 300 ? 40 : h2 < 400 ? 60 : 70;
        const rxMult = isLaptop ? 0.44 : 0.40;
        const ryMult = isLaptop ? 0.42 : 0.36;
        const rx = Math.max(w2 * rxMult, minRx);
        const ry = Math.max(h2 * ryMult, minRy);

        const tl = gsap.timeline({
          onComplete: startBubble,
        });

        logoRefs.current.forEach((el, i) => {
          if (!el) return;
          const angle = (i / n) * 2 * Math.PI - Math.PI / 2;
          const x = cx2 + rx * Math.cos(angle);
          const y = cy2 + ry * Math.sin(angle);

          tl.to(
            el,
            {
              x,
              y,
              scale: 1,
              opacity: 1,
              duration: 1.2,
              ease: 'back.out(1.2)',
              xPercent: -50,
              yPercent: -50,
            },
            i * 0.06
          );
        });
      },
      { threshold: 0.15, rootMargin: '0px' }
    );

    observer.observe(section);

    const handleResize = () => {
      if (!hasPoppedRef.current) return;
      const { centerX: cx3, centerY: cy3, width: w3, height: h3 } = getTargets();
      const n = partnerLogos.length;
      const isLaptop = typeof window !== 'undefined' && window.innerWidth >= 1024 && window.innerWidth < 1536;
      const minRx = w3 < 400 ? 50 : w3 < 640 ? 80 : 100;
      const minRy = h3 < 300 ? 40 : h3 < 400 ? 60 : 70;
      const rxMult = isLaptop ? 0.44 : 0.40;
      const ryMult = isLaptop ? 0.42 : 0.36;
      const rx = Math.max(w3 * rxMult, minRx);
      const ry = Math.max(h3 * ryMult, minRy);
      logoRefs.current.forEach((el, i) => {
        if (!el) return;
        const angle = (i / n) * 2 * Math.PI - Math.PI / 2;
        gsap.set(el, {
          x: cx3 + rx * Math.cos(angle),
          y: cy3 + ry * Math.sin(angle),
          xPercent: -50,
          yPercent: -50,
          scale: 1,
          opacity: 1,
        });
      });
    };

    window.addEventListener('resize', handleResize);
    const currentLogoRefs = logoRefs.current;
    const currentBubbleTweens = bubbleTweensRef.current;
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
      currentBubbleTweens.forEach((t) => t && t.kill());
      currentLogoRefs.forEach((el) => el && gsap.killTweensOf(el));
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full flex items-center justify-center overflow-hidden bg-[#ebe6e0] min-h-[50vh] lg:min-h-[62vh] 2xl:min-h-[50vh]"
      style={{ boxSizing: 'border-box' }}
    >
      {/* Tagline - center, surrounded by tickets on ellipse; gradient text like GetStarted */}
      <div className="relative z-10 text-center px-4 pointer-events-none overflow-visible pb-2">
        <h2 className="text-lg sm:text-2xl md:text-3xl lg:text-3xl xl:text-4xl 2xl:text-5xl font-extrabold tracking-tight leading-snug text-transparent bg-clip-text bg-gradient-to-r from-brand to-accent">
          Proudly Working With
        </h2>
      </div>

      {/* Tickets on ellipse around tagline - one per partner */}
      <div className="absolute inset-0 pointer-events-none">
        {partnerLogos.map((partner, index) => (
          <div
            key={partner.name}
            ref={(el) => (logoRefs.current[index] = el)}
            className={`absolute left-0 top-0 flex items-center justify-center opacity-95 hover:opacity-100 transition-opacity pointer-events-auto p-1.5 sm:p-2 md:p-2 lg:p-1.5 xl:p-2 2xl:p-2.5 rounded-lg sm:rounded-xl shadow-lg border w-[70px] h-[36px] sm:w-[100px] sm:h-[50px] md:w-[110px] md:h-[55px] lg:w-[120px] lg:h-[60px] xl:w-[140px] xl:h-[70px] 2xl:w-[180px] 2xl:h-[90px] ${
              partner.ticketStyle === 'dark'
                ? 'bg-ticket-dark border-ticket-dark-border/80 shadow-xl'
                : partner.ticketStyle === 'accent'
                ? 'bg-gradient-to-br from-amber-50 to-amber-100/90 border-amber-200/60 shadow-xl'
                : 'bg-white/95 border-black/5'
            }`}
          >
            <img
              src={partner.logo}
              alt={partner.name}
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                const fallback = e.target.nextElementSibling;
                if (fallback) fallback.classList.remove('hidden');
              }}
            />
            <span className="hidden text-ink font-semibold text-sm text-center px-2 absolute inset-0 flex items-center justify-center">
              {partner.name}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Partners;
